import { HttpParams } from '@angular/common/http';
import {
  JsonapiEntity,
  MetaObject,
  PagedTopLevelData,
  PaginationLinksObject,
  ResourceIdentifier,
  ResourceIdentifierConstructor,
  TopLevelData,
  TopLevelDatum,
  deserialiseMany,
  deserialiseOne,
  isEntityConstructorRegistered,
  toJsonApi,
} from '@talisdev/jsonapi-transformers';

export interface PageOf<T extends JsonapiEntity<T>> {
  data: T[];
  links: PaginationLinksObject;
  meta: MetaObject;
}

export interface EntityWithMeta<T extends JsonapiEntity<T>> {
  entity: T;
  meta: MetaObject;
}

interface QueryParams {
  include?: string[];
}
interface PageQueryParams extends QueryParams {
  searchTerm?: string;
  limit?: number;
  offset?: number;
}
export type JsonapiQueryParams = QueryParams;
export type JsonapiPageQueryParams = PageQueryParams;

export function convertQueryParamsToHttpParams(
  params: JsonapiQueryParams,
  httpParams: HttpParams = new HttpParams(),
): HttpParams {
  if (params.include) {
    httpParams = httpParams.set('include', params.include.sort().join(','));
  }
  return httpParams;
}

export function convertPageQueryParamsToHttpParams(
  params: JsonapiPageQueryParams,
  httpParams: HttpParams = new HttpParams(),
): HttpParams {
  httpParams = convertQueryParamsToHttpParams(params, httpParams);
  if (params.searchTerm) {
    httpParams = httpParams.set('filter[search_term]', params.searchTerm);
  }
  if (params.limit) {
    httpParams = httpParams.set('page[limit]', params.limit.toString());
  }
  if (params.offset) {
    httpParams = httpParams.set('page[offset]', params.offset.toString());
  }
  return httpParams;
}

export function convertToJsonapiResponseDatum<T extends JsonapiEntity<T>>(
  topLevelDatum: TopLevelDatum,
): T {
  return deserialiseOne<T>(topLevelDatum);
}

export function convertToJsonapiResponseData<T extends JsonapiEntity<T>>(
  topLevelData: TopLevelData,
): T[] {
  return deserialiseMany<T>(topLevelData);
}

export function convertToJsonApiPagedResponseData<T extends JsonapiEntity<T>>(
  pagedTopLevelData: PagedTopLevelData,
): PageOf<T> {
  const { links, meta } = pagedTopLevelData;
  return {
    data: convertToJsonapiResponseData<T>(pagedTopLevelData),
    links,
    meta,
  };
}

export function getDeserialisedDatumOrOriginalEntity<
  T extends JsonapiEntity<T>,
>(entity: T) {
  return (response: TopLevelDatum | null): T =>
    response ? convertToJsonapiResponseDatum<T>(response) : entity;
}

export function getDeserialisedDatumOrOriginalEntityWithMeta<
  T extends JsonapiEntity<T>,
>(originalEntity: T) {
  return (response: TopLevelDatum | null): EntityWithMeta<T> => {
    const meta = (response && response.meta) || null;
    const entity =
      getDeserialisedDatumOrOriginalEntity(originalEntity)(response);
    return { entity, meta };
  };
}

/**
 * Wrap a single JSON API entity into a request body.
 * @param {JsonapiEntity} entity The entity to include in the body.
 * @returns {TopLevelDatum} A JSON-API compliant single entity body.
 */
export function convertEntityToJsonapiBody<T extends JsonapiEntity<T>>(
  entity: T,
): TopLevelDatum {
  return { data: toJsonApi(entity) };
}

/**
 * Wrap an array of JSON API resource identifiers into a request body.
 * @param {ResourceIdentifier} identifiers The identifiers to include in the body.
 * @returns {TopLevelData} A JSON-API compliant body containing multiple identifiers.
 */
export function convertResourceIdentifiersToJsonapiBody(
  identifiers: ResourceIdentifier[],
): TopLevelData {
  return { data: identifiers.map(toJsonApi) };
}

/**
 * @talisdev/jsonapi-transformers uses decorators to register JSON:API types to JS classes.
 * However, due to lazy-loading and Typescript's absence from runtime, any entity
 * that is not constructed (`new MyEntity()`) will not run the constructor or
 * the `@entity` decorator, so the registration will not happen.
 *
 * This method allows us to ensure that a particular constructor is registered
 * before using it.
 */
export function ensureJsonapiEntityIsRegistered(
  entityConstructor: ResourceIdentifierConstructor,
): void {
  if (!isEntityConstructorRegistered(entityConstructor)) {
    throw new Error(`JSON:API type not registered: ${entityConstructor.name}`);
  }
}
